import { FloorPlanRoomSchema } from '@kaiizen/kai-floor-plan';
import {
  boolean,
  number,
  optional,
  optionalBoolean,
  optionalNumber,
  optionalString,
  string,
  zodLess,
  ZodLessInfer,
  zrecord,
  zunion,
} from '@kaiizen/zod-less';
import { CustomerMetadataSchema, ScanInlineProductSchema, ScanTimestampsSchema, ScanValueTypeSchema } from '../CommonScanSchemas';
import { ProgressUpdateSchema } from './ProgressUpdateSchema';

export type ScanPartContext = {
  groupId?: string;
  taskId?: string;
  optionId?: string;
  lineItemId?: string;
};

export const ScanPhotoSourceSchema = zodLess('camera|upload');
export type ScanPhotoSource = ZodLessInfer<typeof ScanPhotoSourceSchema>;
export const ScanPhotoSchema = zodLess({
  photoId: string,
  addedAt: optionalNumber,
  addedByUserId: optionalString,
  tags: optional([string]),
  photoSource: optional(ScanPhotoSourceSchema),

  // video-to-scope provenance (only set when photo came from VideoToScopeJob)
  scanVideoId: optionalString, // ScanVideo.id — canonical provenance field
  orgVideoId: optionalString, // @deprecated legacy field — read-only for old scan data; use scanVideoId
  videoFrameTimeSeconds: optionalNumber, // offset in the source video, in seconds
});
export type ScanPhoto = ZodLessInfer<typeof ScanPhotoSchema>;

// A time range into the ORIGINAL PARENT walkthrough's own timeline. It is always a sub-object of a
// ScanVideoSchema, so it carries no id of its own — the enclosing ScanVideo's `videoId` is the
// reference. INVARIANT: this range is always parent-relative (seconds from the start of the parent
// walkthrough), never clip-local or recording-local. Full walkthrough uploads use
// { startSeconds: 0, endSeconds: <total duration> }; group/task segments use the "interesting"
// range into the parent.
export const VideoOffsetSchema = zodLess({
  startSeconds: number,
  endSeconds: number,
});
export type VideoOffset = ZodLessInfer<typeof VideoOffsetSchema>;

export const ScanVideoSourceSchema = zodLess('camera|upload');
export type ScanVideoSource = ZodLessInfer<typeof ScanVideoSourceSchema>;
export const ScanVideoSchema = zodLess({
  videoId: string, // ScanVideo.id
  addedAt: optionalNumber,
  addedByUserId: optionalString,
  tags: optional([string]),
  videoSource: optional(ScanVideoSourceSchema),

  // INVARIANT: `videoId` always references the ORIGINAL PARENT walkthrough video, and `videoOffset`
  // is the segment's range on that parent's timeline. A materialized clip (its own S3/ScanVideo row)
  // is discoverable via `ScanVideo.parentScanVideoId`, but scan data still points at the parent and
  // its parent-relative range — offsets are never expressed in a clip's local coordinate space.
  videoOffset: optional(VideoOffsetSchema),
});
export type ScanVideo = ZodLessInfer<typeof ScanVideoSchema>;

export const ScanAudioSourceSchema = zodLess('fieldExtract|userRecording');
export type ScanAudioSource = ZodLessInfer<typeof ScanAudioSourceSchema>;

// Interval on the ORIGINAL PARENT walkthrough's timeline this audio segment covers. Always a
// sub-object of ScanAudioSchema; the enclosing ScanAudio's `audioId` is the reference to the
// ScanVideoAudio blob row. INVARIANT: parent-relative seconds, never 0-based into a user recording.
// A user-recorded narration is placed on the parent by the video time at which it was recorded
// (ScanVideoAudio.startSeconds/endSeconds); a field-extract track spans the whole parent (starts 0).
export const AudioOffsetSchema = zodLess({
  startSeconds: number,
  endSeconds: number,
});
export type AudioOffset = ZodLessInfer<typeof AudioOffsetSchema>;

export const ScanAudioSchema = zodLess({
  audioId: string, // ScanVideoAudio.id
  scanVideoId: string, // ScanVideo.id this narration is scoped to
  addedAt: optionalNumber,
  addedByUserId: optionalString,
  tags: optional([string]),
  audioSource: optional(ScanAudioSourceSchema),
  audioOffset: optional(AudioOffsetSchema),
});
export type ScanAudio = ZodLessInfer<typeof ScanAudioSchema>;

// ************************************************************************
// Scan Value Schema
// ************************************************************************
// values can either be calculated from the template or set directly
// "set" is synonymous with "overridden"
export const ScanValueSchema = zodLess({
  /*
  calculatedValue is the value that comes from the template or from the result of the template's expression;
  the setValue, if present, will be used instead of the calculatedValue.
  checkbox values are stored as booleans in both fields; the calculation engine computes them as 1/0
  */
  calculatedValue: optional(ScanValueTypeSchema), // values that are the default of calculated using the template's expression
  setValue: optional(ScanValueTypeSchema), // values that are set directly by the user
});
export type ScanValue = ZodLessInfer<typeof ScanValueSchema>;

export const ScanValuesSchema = zrecord(ScanValueSchema);
export type ScanValues = ZodLessInfer<typeof ScanValuesSchema>;

/**
 * WellDefinedValueTypes - although values can have any name, we have a few that are well defined and have special meaning
 */
type WellDefinedValueTypes =
  | 'price' // price is the price per unit for a SKU and gets injected (as a "setValue") when the SKU is selected
  | 'materialCost' // materialCost can be any calculation, but it is what is READ in the UX and reports to show the cost of the material
  | 'laborCost' // laborCost can be any calculation, but it is what is READ in the UX and reports to show the cost of the labor
  | 'roomArea'; // roomArea can be any calculation, but it is what is READ in the UX and reports to show the area of the room

// ************************************************************************
// Scan Model Schemas
// ************************************************************************
// Common fields for all scan parts
export const ScanPartSchema = ScanTimestampsSchema.extend({
  id: string,
  templateId: string,
  customLabel: optionalString,
  values: optional(ScanValuesSchema),
  photos: optional([ScanPhotoSchema]),
  notApplicable: optionalBoolean,
  assignedContractorId: string.optional().nullable(), // task-only - supports null for "use default"
  skuId: optionalString,
  skuDetails: optional(ScanInlineProductSchema),
  progressUpdates: optional([ProgressUpdateSchema]),

  customerMetadata: optional(CustomerMetadataSchema),

  // in the future notes will only be used for exports
  roomFloorPlan: optional(FloorPlanRoomSchema),

  tags: optional(zrecord(zunion(string, number, boolean))),

  // true if the scan part was added by the user (not part of the template)
  userAdded: optionalBoolean,

  // agent update is any update to the scan part by the agent during VideoToScopeJob
  agentUpdatedAt: optionalNumber, // unix timestamp in milliseconds (same as createdAt/updatedAt)
  agentUpdateAcceptedAt: optionalNumber,
});

export const ScanLineItemSchema = ScanPartSchema.extend({}).meta({
  id: 'ScanLineItem',
  description: 'ScanLineItem is the 5th and lowest level of the scan data hierarchy. It has no children.',
});
export const ScanOptionSchema = ScanPartSchema.extend({ lineItems: optional([ScanLineItemSchema]) }).meta({
  id: 'ScanOption',
  description: 'ScanOption is the 4th level of the scan data hierarchy. Its children are ScanLineItems.',
});
export const ScanTaskSchema = ScanPartSchema.extend({
  trade: optionalString,
  options: optional([ScanOptionSchema]),
  videos: optional([ScanVideoSchema]),
  audio: optional([ScanAudioSchema]),
}).meta({ id: 'ScanTask', description: 'ScanTask is the 3rd level of the scan data hierarchy. Its children are ScanOptions.' });
export const ScanGroupSchema = ScanPartSchema.extend({
  tasks: optional([ScanTaskSchema]),
  videos: optional([ScanVideoSchema]),
  audio: optional([ScanAudioSchema]),
}).meta({
  id: 'ScanGroup',
  description: 'ScanGroup is the 2nd level of the scan data hierarchy. Its children are ScanTasks.',
});

const HomeFactFields = {
  bedroomCount: optionalNumber,
  bathroomCount: optionalNumber,
  fullBathCount: optionalNumber,
  halfBathCount: optionalNumber,
  squareFeet: optionalNumber,
  vintage: optionalNumber,
  acres: optionalNumber,
  hasGarage: optionalBoolean,
};

export const ContractorIdsByTradeSchema = zrecord(string.nullable());
export type ContractorIdsByTrade = ZodLessInfer<typeof ContractorIdsByTradeSchema>;

export const ScanRootSchema = ScanPartSchema.extend({
  groups: optional([ScanGroupSchema]),
  videos: optional([ScanVideoSchema]),
  audio: optional([ScanAudioSchema]),
  defaultContractorsByTrade: optional(ContractorIdsByTradeSchema), // Record<string, string> - trade slug -> contractorId
  ...HomeFactFields,
}).meta({ id: 'ScanRoot', description: 'The root of the scan data. This is the main object that contains all the data for a scan.' });

export const ScanHomeFactsSchema = zodLess(HomeFactFields);
export type ScanHomeFacts = ZodLessInfer<typeof ScanHomeFactsSchema>;

/**
 * @deprecated use ScanRootSchema instead
 */
export const ScanDataSchema = ScanRootSchema;

// ************************************************************************
// Scan Model Types
// ************************************************************************

// Common base type for all scan parts
export type ScanPart = ZodLessInfer<typeof ScanPartSchema>;

// Specific types for each scan part type
export type ScanLineItem = ZodLessInfer<typeof ScanLineItemSchema>;
export type ScanOption = ZodLessInfer<typeof ScanOptionSchema>;
export type ScanTask = ZodLessInfer<typeof ScanTaskSchema>;
export type ScanGroup = ZodLessInfer<typeof ScanGroupSchema>;
export type ScanRoot = ZodLessInfer<typeof ScanRootSchema>;

/**
 * @deprecated use ScanRoot instead
 */
export type ScanData = ScanRoot;
